import React, { useState } from 'react';

export type AnswerPillState = 'default' | 'selected' | 'correct' | 'pending';

interface AnswerPillProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  state?: AnswerPillState;
  onToggleCorrect?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  /**
   * Render `value` as a static `<span>` instead of an editable input.
   * Useful for fixed labels like True/False where the text shouldn't change
   * but the toggle must remain interactive.
   */
  staticLabel?: boolean;
  leadingIcon?: React.ReactNode;
  trailingLabel?: string;
  className?: string;
}

/**
 * AnswerPill — 46px rounded-[50px] pill with state-driven styling.
 *
 * Matches Figma node 11126:525051 (default); the selected/correct state is
 * success-themed so authors can't mistake it for anything but "correct":
 * - default: 1px grey border, transparent bg, empty circle indicator
 * - selected/correct: 1px green border, success/10% bg, filled green disc
 *   with a white check mark, green glow shadow
 * - pending: default border + info-coloured ring
 */
export default function AnswerPill({
  value,
  onChange,
  placeholder = 'Type your answer…',
  state = 'default',
  onToggleCorrect,
  onRemove,
  disabled,
  readOnly,
  staticLabel = false,
  leadingIcon,
  trailingLabel,
  className = '',
}: AnswerPillProps) {
  const [focused, setFocused] = useState(false);
  const isSelected = state === 'selected' || state === 'correct';

  const containerStateClass = isSelected
    ? 'border-success-500 bg-success-500/10 shadow-[0_0_20px_0_theme(colors.success.500/20%)]'
    : state === 'pending'
      ? 'border-warning-500 bg-transparent'
      : focused
        ? 'border-primary-500 bg-transparent'
        : 'border-text-secondary bg-transparent';

  const inputColorClass = isSelected
    ? 'text-success-700 dark:text-success-400'
    : 'text-text-primary';

  const indicatorClass = isSelected
    ? 'border-success-500 bg-success-500'
    : 'border-text-secondary bg-transparent';

  return (
    <div
      className={`flex items-center w-full h-[46px] gap-2.5 py-2.5 pl-[18px] pr-3.5 rounded-[50px] border transition-all duration-150 ease-out ${containerStateClass} ${className}`}
    >
      {leadingIcon && <div className="flex-shrink-0">{leadingIcon}</div>}
      {staticLabel ? (
        <span
          className={`flex-1 min-w-0 font-sans text-sm leading-[22px] font-normal ${inputColorClass}`}
        >
          {value}
        </span>
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          className={`flex-1 min-w-0 bg-transparent border-0 outline-none font-sans text-sm leading-[22px] font-normal ${inputColorClass}`}
        />
      )}
      {trailingLabel && (
        <span className="flex-shrink-0 font-sans text-xs font-medium text-text-secondary whitespace-nowrap">
          {trailingLabel}
        </span>
      )}
      {onToggleCorrect && (
        <button
          type="button"
          onClick={onToggleCorrect}
          disabled={disabled || readOnly}
          aria-label="Toggle correct answer"
          aria-pressed={isSelected}
          className={`flex-shrink-0 flex items-center justify-center w-5 h-5 p-0 rounded-full border-[1.5px] transition-all duration-150 ease-out ${indicatorClass} ${disabled || readOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {isSelected && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 12.5l4.5 4.5L19 7.5"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      )}
      {onRemove && !readOnly && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove answer"
          className="flex-shrink-0 flex items-center justify-center w-5 h-5 p-0 border-0 bg-transparent text-text-secondary hover:text-error-500 transition-colors duration-150 cursor-pointer"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
